// PhaseContainer — journey-phase grouping section for the chronological
// timeline redesign.
//
// Groups a set of granular TripSteps under one Journey Phase (Departure,
// Destination, or Return). Replicates the CloudScape `Container` + `Header`
// visual using our existing `designTokens` and the sibling `Badge` / `TripStep`
// primitives.
//
// Structure (per design's PhaseContainer section):
//   - A semantic <section> with an aria-label describing the phase and its step
//     count, e.g. "Departure phase, 4 steps" (Req 11.1, 11.3).
//   - A header with the phase title as an <h2>, a description summarizing the
//     date/scope of the phase, and a colored phase Badge —
//     Departure→blue "Outbound", Destination→green "Destination",
//     Return→red "Return" (Req 2.2–2.5).
//   - Steps rendered as an ordered list <ol>, each TripStep wrapped in an <li>
//     (Req 11.2), always visible with no accordion / collapse / expand toggle
//     (Req 2.7).
//
// Consumes a single `phase` object ({ id, title, description, badge, steps })
// produced by the pure logic layer (groupIntoPhases → buildTimelineModel).
//
// Validates: Requirements 2.2, 2.3, 2.4, 2.5, 2.7, 11.1, 11.2, 11.3

import React from 'react';

import {
  colors,
  typography,
  fontWeights,
  spacing,
  cardStyles,
} from '../../../styles/designTokens';
import { Badge } from './Badge';
import { TripStep } from './TripStep';

/**
 * @typedef {'departure'|'destination'|'return'} PhaseId
 *
 * @typedef {Object} PhaseBadge
 * @property {'blue'|'green'|'red'|'grey'} color
 * @property {string} label
 *
 * @typedef {Object} TripStepData
 * @property {string} id
 * @property {string} time
 * @property {string} status
 * @property {string} icon
 * @property {string} title
 * @property {string} description
 * @property {Array<{label:string,value:React.ReactNode}>} details
 * @property {string} tip
 *
 * @typedef {Object} Phase
 * @property {PhaseId} id
 * @property {string} title       - Phase name, e.g. "Departure"
 * @property {string} description - Date-scoped summary of the phase
 * @property {PhaseBadge} badge   - Colored phase badge
 * @property {Array<TripStepData>} steps - Steps sorted ascending by scheduledStart
 */

/**
 * @typedef {Object} PhaseContainerProps
 * @property {Phase} phase        - The phase view model to render.
 * @property {boolean} [isMobile] - Collapses each TripStep to a single column.
 */

/**
 * PhaseContainer renders one journey phase as a semantic section containing an
 * ordered list of always-visible TripSteps. Returns null for a falsy phase.
 *
 * @param {PhaseContainerProps} props
 * @returns {React.ReactElement|null}
 */
export function PhaseContainer({ phase, isMobile = false }) {
  if (!phase || typeof phase !== 'object') {
    return null;
  }

  const { id, title, description, badge, steps } = phase;
  const stepList = Array.isArray(steps) ? steps : [];
  const stepCount = stepList.length;

  return (
    <section
      data-testid={`phase-container-${id}`}
      aria-label={`${title} phase, ${stepCount} steps`}
      style={cardStyles.container}
    >
      <div
        style={{
          ...cardStyles.header,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          rowGap: spacing.elementGap,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            minWidth: 0,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: typography.subheading.fontSize,
              fontWeight: fontWeights.semibold,
              color: colors.cardHeaderText,
            }}
          >
            {title}
          </h2>
          {description ? (
            <span
              style={{
                fontSize: typography.caption.fontSize,
                fontWeight: fontWeights.regular,
                color: colors.cardHeaderText,
                opacity: 0.8,
              }}
            >
              {description}
            </span>
          ) : null}
        </div>
        {badge ? (
          <div style={{ flexShrink: 0 }}>
            <Badge color={badge.color}>{badge.label}</Badge>
          </div>
        ) : null}
      </div>

      <ol
        style={{
          ...cardStyles.body,
          listStyle: 'none',
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.cardGap,
        }}
      >
        {stepList.map((step) => (
          <li key={step.id} style={{ margin: 0 }}>
            <TripStep
              time={step.time}
              status={step.status}
              icon={step.icon}
              title={step.title}
              description={step.description}
              details={step.details}
              tip={step.tip}
              isMobile={isMobile}
            />
          </li>
        ))}
      </ol>
    </section>
  );
}

export default PhaseContainer;
